import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RoleAdaptiveNavbar from '../../components/ui/RoleAdaptiveNavbar';
import GalleryNavigationBreadcrumb from '../../components/ui/GalleryNavigationBreadcrumb';
import Icon from '../../components/AppIcon';


import GalleryHeader from './components/GalleryHeader';
import FilterBar from './components/FilterBar';
import MediaGrid from './components/MediaGrid';
import MediaViewer from './components/MediaViewer';
import VisitorRegistrationModal from './components/VisitorRegistrationModal';

import { galleryService } from '../../services/galleryService';
import { mediaService } from '../../services/mediaService';
import { supabase } from '../../lib/supabase';

const GalleryViewer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const galleryId = searchParams?.get('id');

  const [isRegistered, setIsRegistered] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [visitorInfo, setVisitorInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaType, setMediaType] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [currentMediaItem, setCurrentMediaItem] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(null);
  const [checkingRegistration, setCheckingRegistration] = useState(true);
  const [isAuthenticatedUser, setIsAuthenticatedUser] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [showMediaInfo, setShowMediaInfo] = useState(true);
  const [mediaData, setMediaData] = useState([]);
  const [filteredMedia, setFilteredMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canDownload, setCanDownload] = useState(false);

  // Check for existing registration on mount
  useEffect(() => {
    const checkExistingRegistration = async () => {
      if (!galleryId) {
        setIsRegistered(true);
        setCheckingRegistration(false);
        setShowMediaInfo(true);
        return;
      }

      try {
        const { data: { session } } = await supabase?.auth?.getSession();

        if (session?.user) {
          const { data: profile } = await supabase?.
          from('user_profiles')?.
          select('role')?.
          eq('id', session?.user?.id)?.
          single();

          setUserRole(profile?.role);

          if (profile?.role === 'user') {
            await supabase?.rpc('record_gallery_access', { p_gallery_id: galleryId });
            setIsAuthenticatedUser(true);
            setIsRegistered(true);
            setCheckingRegistration(false);
            return;
          } else if (profile?.role === 'owner' || profile?.role === 'contributor') {
            setIsAuthenticatedUser(true);
            setIsRegistered(true);
            setCheckingRegistration(false);
            return;
          }
        }

        navigate(`/user-registration?returnUrl=${encodeURIComponent(`/gallery-viewer?id=${galleryId}`)}`);
      } catch (error) {
        console.error('Error checking authentication:', error);
        navigate(`/user-registration?returnUrl=${encodeURIComponent(`/gallery-viewer?id=${galleryId}`)}`);
      } finally {
        setCheckingRegistration(false);
      }
    };

    checkExistingRegistration();
  }, [galleryId, navigate]);

  // Fetch gallery data and media
  useEffect(() => {
    const fetchGalleryData = async () => {
      if (!galleryId || !isRegistered) return;

      setLoading(true);
      try {
        const [gallery, media] = await Promise.all([
        galleryService?.getGalleryById(galleryId),
        mediaService?.getGalleryMedia(galleryId, { sortBy, mediaType: mediaType === 'all' ? null : mediaType })]
        );

        setGalleryData(gallery);
        setShowMediaInfo(gallery?.show_media_info ?? true);

        // Determine download permissions based on role and gallery settings
        const downloadAllowed = gallery?.download_enabled && (
        userRole === 'owner' || userRole === 'contributor' || userRole === 'user');
        setCanDownload(downloadAllowed);

        const transformedMedia = media?.map((m) => ({
          id: m?.id,
          type: m?.media_type === 'video' ? 'video' : 'image',
          title: m?.title || m?.file_name,
          description: m?.description,
          thumbnail: m?.thumbnail_url || m?.file_url,
          thumbnailAlt: m?.alt_text,
          url: m?.file_url,
          size: m?.file_size,
          uploadDate: m?.created_at,
          downloadUrl: m?.drive_web_view_link || m?.file_url,
          downloadEnabled: m?.download_enabled && downloadAllowed,
          duration: 0
        }));

        setMediaData(transformedMedia);
        setFilteredMedia(transformedMedia);
      } catch (error) {
        console.error('Error fetching gallery data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, [galleryId, isRegistered, sortBy, mediaType, userRole]);

  // Filter and search media
  useEffect(() => {
    let filtered = [...mediaData];

    // Apply search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter((item) =>
      item?.title?.toLowerCase()?.includes(query) ||
      item?.description?.toLowerCase()?.includes(query)
      );
    }

    setFilteredMedia(filtered);
  }, [searchQuery, mediaData]);

  // Handle media selection
  const handleMediaSelect = useCallback((mediaId) => {
    setSelectedMedia((prev) => {
      if (prev?.includes(mediaId)) {
        return prev?.filter((id) => id !== mediaId);
      }
      return [...prev, mediaId];
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (selectedMedia?.length === filteredMedia?.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(filteredMedia?.map((m) => m?.id));
    }
  }, [selectedMedia, filteredMedia]);

  // Handle media click to open viewer
  const handleMediaClick = useCallback((media) => {
    const index = filteredMedia?.findIndex((m) => m?.id === media?.id);
    setCurrentMediaItem(media);
    setCurrentMediaIndex(index);

    // Increment view count
    if (media?.id) {
      mediaService?.incrementViewCount(media?.id);
    }
  }, [filteredMedia]);

  // Navigate to next media
  const handleNextMedia = useCallback(() => {
    if (currentMediaIndex !== null && currentMediaIndex < filteredMedia?.length - 1) {
      const nextIndex = currentMediaIndex + 1;
      const nextMedia = filteredMedia?.[nextIndex];
      setCurrentMediaItem(nextMedia);
      setCurrentMediaIndex(nextIndex);

      if (nextMedia?.id) {
        mediaService?.incrementViewCount(nextMedia?.id);
      }
    }
  }, [currentMediaIndex, filteredMedia]);

  // Navigate to previous media
  const handlePreviousMedia = useCallback(() => {
    if (currentMediaIndex !== null && currentMediaIndex > 0) {
      const prevIndex = currentMediaIndex - 1;
      const prevMedia = filteredMedia?.[prevIndex];
      setCurrentMediaItem(prevMedia);
      setCurrentMediaIndex(prevIndex);

      if (prevMedia?.id) {
        mediaService?.incrementViewCount(prevMedia?.id);
      }
    }
  }, [currentMediaIndex, filteredMedia]);

  // Close media viewer
  const handleCloseViewer = useCallback(() => {
    setCurrentMediaItem(null);
    setCurrentMediaIndex(null);
  }, []);

  // Handle single media download
  const handleDownloadMedia = useCallback(async (media) => {
    if (!media?.downloadEnabled) return;

    try {
      // Increment download count
      await mediaService?.incrementDownloadCount(media?.id);

      // Open download URL in new tab
      window.open(media?.downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading media:', error);
    }
  }, []);

  // Handle bulk download
  const handleDownloadSelected = useCallback(async () => {
    if (selectedMedia?.length === 0) return;

    try {
      const mediaToDownload = filteredMedia?.filter((m) =>
      selectedMedia?.includes(m?.id) && m?.downloadEnabled
      );

      // Increment download counts
      await Promise.all(
        mediaToDownload?.map((m) => mediaService?.incrementDownloadCount(m?.id))
      );

      // Open each download URL
      mediaToDownload?.forEach((media, index) => {
        setTimeout(() => {
          window.open(media?.downloadUrl, '_blank');
        }, index * 500); // Stagger downloads to avoid popup blocking
      });

      setSelectedMedia([]);
    } catch (error) {
      console.error('Error downloading selected media:', error);
    }
  }, [selectedMedia, filteredMedia]);

  // Handle visitor registration
  const handleVisitorRegistration = useCallback((formData) => {
    setVisitorInfo(formData);
    setIsRegistered(true);
    setShowRegistrationModal(false);
  }, []);

  if (checkingRegistration) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-4" color="var(--color-primary)" />
          <p className="text-muted-foreground">Loading gallery...</p>
        </div>
      </div>);

  }

  if (!isRegistered) {
    return null;
  }

  // Mock data for preview mode
  const mockGallery = {
    id: 'gallery-001',
    title: 'Summer Wedding Collection 2026',
    description: 'Beautiful moments captured from Sarah & Michael\'s wedding ceremony at Sunset Gardens.',
    coverImage: "https://images.unsplash.com/photo-1729017537692-7adbe092810a",
    coverImageAlt: 'Elegant outdoor wedding ceremony setup',
    totalMedia: 156,
    photoCount: 132,
    videoCount: 24,
    createdDate: '2026-01-10',
    downloadEnabled: true
  };

  const mockMedia = [
  {
    id: 'media-001',
    type: 'image',
    title: 'Ceremony Entrance',
    description: 'Bride walking down the aisle with father',
    thumbnail: "https://images.unsplash.com/photo-1732053910501-11cb2fce2cf9",
    thumbnailAlt: 'Bride walking down outdoor aisle',
    url: "https://images.unsplash.com/photo-1732053910501-11cb2fce2cf9",
    size: 4567890,
    uploadDate: '2026-01-10T14:30:00',
    downloadEnabled: true
  },
  {
    id: 'media-002',
    type: 'video',
    title: 'First Dance',
    description: 'Couple\'s romantic first dance',
    thumbnail: "https://images.unsplash.com/photo-1712948222862-1f4ec8bd8416",
    thumbnailAlt: 'Newlywed couple dancing together',
    url: 'https://sample-videos.com/video123/mp4/720/big_buck_bunny_720p_1mb.mp4',
    size: 15678900,
    duration: 245,
    uploadDate: '2026-01-10T19:15:00',
    downloadEnabled: true
  }];


  const displayGallery = galleryData || mockGallery;
  const displayMedia = filteredMedia?.length > 0 ? filteredMedia : mockMedia;

  return (
    <div className="min-h-screen bg-background">
      <RoleAdaptiveNavbar />
      
      <div className="container mx-auto px-4 py-6 md:py-8">
        <GalleryNavigationBreadcrumb
          items={[
          { label: 'Home', path: '/' },
          { label: 'Gallery Viewer', path: '/gallery-viewer' },
          { label: displayGallery?.title || 'Gallery' }]
          } />


        <div className="mt-6 space-y-6">
          <GalleryHeader gallery={displayGallery} />

          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            mediaType={mediaType}
            onMediaTypeChange={setMediaType}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            selectedCount={selectedMedia?.length}
            onClearSelection={() => setSelectedMedia([])}
            onDownloadSelected={handleDownloadSelected}
            onSelectAll={handleSelectAll}
            totalCount={displayMedia?.length}
            canDownload={canDownload} />


          {loading ?
          <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
            </div> :
          displayMedia?.length === 0 ?
          <div className="text-center py-12">
              <Icon name="ImageOff" size={64} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">No media found</h3>
              <p className="text-muted-foreground">
                {searchQuery ? 'Try adjusting your search or filters' : 'This gallery is empty'}
              </p>
            </div> :

          <MediaGrid
            media={displayMedia}
            selectedMedia={selectedMedia}
            onMediaSelect={handleMediaSelect}
            onMediaClick={handleMediaClick}
            viewMode={viewMode}
            showMediaInfo={showMediaInfo} />

          }
        </div>
      </div>

      {currentMediaItem &&
      <MediaViewer
        media={currentMediaItem}
        onClose={handleCloseViewer}
        onNext={handleNextMedia}
        onPrevious={handlePreviousMedia}
        onDownload={handleDownloadMedia}
        canDownload={currentMediaItem?.downloadEnabled}
        showMediaInfo={showMediaInfo} />

      }

      {showRegistrationModal &&
      <VisitorRegistrationModal
        onSubmit={handleVisitorRegistration}
        onClose={() => setShowRegistrationModal(false)}
        galleryTitle={displayGallery?.title}
        galleryId={galleryId} />

      }
    </div>);

};

export default GalleryViewer;